export const immersiveKnowledgeNarrationAnchors = [
  "upper-left",
  "upper-right",
  "middle-left",
  "middle-right",
  "lower-left",
  "lower-right",
] as const;

export type ImmersiveKnowledgeNarrationAnchor = (typeof immersiveKnowledgeNarrationAnchors)[number];

/** Synchronization metadata for an approved, offline-packaged narration track. */
export interface FormalNarrationTimeCue {
  readonly id: string;
  readonly startMs: number;
  readonly endMs: number;
  readonly text: string;
}

/** Source contract for a reviewed and offline-packaged audio file. */
export interface FormalNarrationSource {
  readonly src: string;
  readonly mimeType?: string | undefined;
  readonly cues?: readonly FormalNarrationTimeCue[] | undefined;
}

export interface ImmersiveKnowledgeNarrationPage {
  readonly id: string;
  readonly heading?: string | undefined;
  /** Keeps the reviewed heading in narration/audit data while optionally hiding its visual strip. */
  readonly displayHeading?: boolean | undefined;
  readonly body: string;
  /** Plain text sent to the preview speech engine. Falls back to heading + body. */
  readonly narrationText?: string | undefined;
  /** Overrides the node-level card placement for this page. */
  readonly anchor?: ImmersiveKnowledgeNarrationAnchor | undefined;
  /** Omit until a reviewed audio file and its cues are present in the offline package. */
  readonly formalAudio?: FormalNarrationSource | undefined;
}

/** Returns the exact plain text consumed by preview speech and future formal-audio production. */
export function resolveNarrationText(page: ImmersiveKnowledgeNarrationPage): string {
  const explicitText = page.narrationText?.trim();
  if (explicitText) return explicitText;

  return [page.heading?.trim(), page.body.trim()].filter(Boolean).join("。 ");
}

const spokenYearDigits: Readonly<Record<string, string>> = {
  "0": "零",
  "1": "一",
  "2": "二",
  "3": "三",
  "4": "四",
  "5": "五",
  "6": "六",
  "7": "七",
  "8": "八",
  "9": "九",
};

function speakYearDigits(value: string): string {
  return [...value].map((digit) => spokenYearDigits[digit] ?? digit).join("");
}

function speakCalendarNumber(value: string): string {
  const number = Number.parseInt(value, 10);
  if (number < 10) return spokenYearDigits[String(number)] ?? String(number);
  if (number === 10) return "十";
  if (number < 20) return `十${spokenYearDigits[String(number % 10)] ?? ""}`;
  const ones = number % 10;
  return `${spokenYearDigits[String(Math.floor(number / 10))] ?? ""}十${
    ones === 0 ? "" : (spokenYearDigits[String(ones)] ?? "")
  }`;
}

/**
 * Derives speech-only text without changing the reviewed on-screen copy.
 * Year tokens are read digit by digit, while four-digit quantities such as
 * “1824次” and “2000余人” remain untouched.
 */
export function normalizeNarrationYearsForSpeech(value: string): string {
  const isoExpanded = value.replace(
    /(?<!\d)((?:19|20)\d{2})-(\d{2})-(\d{2})(?!\d)/gu,
    (_match, year: string, month: string, day: string) =>
      `${speakYearDigits(year)}年${speakCalendarNumber(month)}月${speakCalendarNumber(day)}日`,
  );

  return isoExpanded.replace(
    /(?<!\d)((?:19|20)\d{2})(?!\d)(?=\s*(?:年|[—–-]\s*(?:19|20)\d{2}|·))/gu,
    (_match, year: string) => speakYearDigits(year),
  );
}

/** Returns the exact text sent to browser or packaged speech engines. */
export function resolveSpeechNarrationText(page: ImmersiveKnowledgeNarrationPage): string {
  return normalizeNarrationYearsForSpeech(resolveNarrationText(page));
}
